'use client';

import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Field } from '../../../auth/field';
import { RiDeleteBinLine } from 'react-icons/ri'; // Assuming you're using react-icons for the delete icon
import { Button } from '@eliadmin/core/src/components/ui/button';
import { FiPlus } from 'react-icons/fi';
import { useEffect } from 'react';

export enum SkillLevels {
  ABSOLUTE_BEGINNER = 'absolute_beginner',
  BEGINNER = 'beginner',
  INTERMEDIATE = 'intermediate',
  ADVANCED = 'advanced',
  EXPERT = 'expert',
}

export const skillLevelsValues = [
  { label: 'text975', value: SkillLevels.ABSOLUTE_BEGINNER },
  { label: 'text976', value: SkillLevels.BEGINNER },
  { label: 'text977', value: SkillLevels.INTERMEDIATE },
  { label: 'text978', value: SkillLevels.ADVANCED },
  { label: 'text979', value: SkillLevels.EXPERT },
];

export const skillValueToLabel = (value: SkillLevels) => {
  const skill = skillLevelsValues.find(skill => skill.value === value);
  return skill ? skill.label : '';
};

export const AddOfferStep1 = () => {
  const { t } = useTranslations();
  const locale = useLocaleStore(state => state.currentLocale);
  const form = useFormContext();

  const {
    fields: experienceFields,
    append: appendExperience,
    remove: removeExperience,
  } = useFieldArray({
    control: form.control,
    name: 'skills',
  });

  useEffect(() => {
    if (!form.getValues('skills')?.length) appendExperience({ name: '', level: '' });
  }, [experienceFields.length, appendExperience]);

  return (
    <div className="flex flex-col gap-6 bg-white p-6 md:gap-8 md:rounded-lg md:p-8">
      <h1 className="text-2xl font-bold md:text-4xl">{t('text1732')}</h1>
      <p className="text-[#63626F]">{t('text1770')}</p>

      {experienceFields.map((field, index) => (
        <div className="grid w-full grid-cols-12 items-center gap-4" key={field.id}>
          {/* Role/Experience Select */}
          <div className="col-span-12 md:col-span-7">
            <Field control={form.control} name={`skills[${index}].name`} label={t('text1774')} />
          </div>

          {/* Level Select */}
          <div className="col-span-10 md:col-span-4">
            <Field
              control={form.control}
              name={`skills[${index}].level`}
              label={t('text1781')}
              type="select"
              options={skillLevelsValues.map(skill => ({
                label: t(skill.label),
                value: skill.value,
              }))}
            />
          </div>

          {/* Remove Button */}
          <div className="col-span-2 mt-6 md:col-span-1">
            <Button
              type="button"
              variant="outline"
              onClick={() => removeExperience(index)}
              disabled={experienceFields.length === 1}
              className="flex h-12 w-12 items-center justify-center rounded-full p-0"
            >
              <RiDeleteBinLine className="h-5 w-5" />
            </Button>
          </div>
        </div>
      ))}

      {/* Add New Experience Button */}
      <Button
        type="button"
        onClick={
          () => appendExperience({ name: '', level: '' }) // Initial empty values for a new experience
        }
        className="mt-4 flex max-w-min gap-2 whitespace-nowrap"
      >
        <FiPlus className="h-5 w-5" /> {t('text1776')}
      </Button>
    </div>
  );
};
